import React from 'react';
import { Box, Stack, Typography, CircularProgress, Alert, Button, Snackbar, Grid, Card, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { calcReputationDelta, getReputationLevel } from '../../../utils/reputationUtils';
import { useSimulationResults } from '../../../hooks/useSimulationResults';
import TrialStatusModal from '../../TrialStatusModal';
import ResultsMetrics from './ResultsMetrics';
import ResultsActions from './ResultsActions';
import ContentCard from '../../ContentCard';
import SummaryCard from '../../SummaryCard';
import ReactionsCard from '../../ReactionsCard';
import CardTitle from '../../common/CardTitle';
import { FancySkeleton, MetricsSkeleton, ReactionSkeletonList } from '../../common/FancySkeleton';
import WaveLoadingAnimation from '../../common/WaveLoadingAnimation';
import SentimentCard from '../../SentimentCard';
import ChartCard from '../../ChartCard';
import ForecastConfidenceCard from '../../ForecastConfidenceCard';

export interface ResultsContainerProps {}

const ResultsContainer: React.FC<ResultsContainerProps> = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    loading,
    simulation,
    effectiveSimId,
    isCreator,
    error,
    // Trial expiration handling
    isExpiredModalOpen,
    isAdmin,
    closeExpiredModal,
    // Alert handling
    alertMessage,
    hideAlert,
    showSuccess,
    showAlert,
    // Actions
    updateSimulation
  } = useSimulationResults();

  // Show loading indicator while fetching
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading simulation results...
        </Typography>
      </Box>
    );
  }

  // Show error if we have an alert but no simulation (and not loading)
  if (!simulation && alertMessage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert severity={alertMessage.type} sx={{ mb: 2 }}>
          {alertMessage.message}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  // If after all checks, there's no simulation and no specific alert, show a generic message
  if (!simulation) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No simulation data found for this ID, or an error occurred while loading.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  // Get the results from the simulation
  const displayResults = simulation.resultJson;

  const isProcessing = !displayResults || simulation.status === 'queued' || simulation.status === 'processing';
  const hasForecast = !!displayResults?.data?.forecast;

  if (isProcessing && !hasForecast) {
    return (
      <Box sx={{ my: 4 }}>
        <Snackbar 
          open={!!alertMessage} 
          autoHideDuration={6000} 
          onClose={hideAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={hideAlert} severity={alertMessage?.type || 'info'} sx={{ width: '100%' }}>
            {alertMessage?.message || ''}
          </Alert>
        </Snackbar>

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom color="text.primary">
            Preparing Your Simulation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We’re generating forecasts and sample reactions. You can browse elsewhere; results will appear here when ready.
          </Typography>
        </Box>

        <Stack spacing={4}>
          {/* Content Preview – show the actual component frame */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: { xs: '100%', sm: '85%', md: '75%', lg: '60%' } }}>
              <ContentCard 
                content={simulation.content || ''}
                platform={simulation.platform || ''}
                sender={simulation.sender || ''}
                imageUrl={simulation.imageUrl || null}
                imageDescription={displayResults.imageDescription}
                replies7d={undefined}
                likes7d={undefined}
                titleComponent={<CardTitle title="Content Preview" tooltip="Your content preview." />}
              />
            </Box>
          </Box>

          {/* Metrics Cards – placeholder cards matching size (no visible gauges) */}
          <Box sx={{ px: { xs: 1.5, md: 0 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ p: 2, height: { xs: 300, md: 360 }, display: 'flex', flexDirection: 'column' }}>
                <CardTitle title="Reputation Impact" tooltip="" />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <WaveLoadingAnimation size={isMobile ? 'medium' : 'large'} />
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ p: 2, height: { xs: 300, md: 360 }, display: 'flex', flexDirection: 'column' }}>
                <CardTitle title="Sentiment Analysis" tooltip="" />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <WaveLoadingAnimation size={isMobile ? 'medium' : 'large'} />
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ p: 2, height: { xs: 300, md: 360 }, display: 'flex', flexDirection: 'column' }}>
                <CardTitle title="Forecast Confidence" tooltip="" />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <WaveLoadingAnimation size={isMobile ? 'medium' : 'large'} />
                </Box>
              </Card>
            </Grid>
          </Grid>
          </Box>

          {/* Detailed analysis skeleton */}
          <Box>
            <CardTitle title="Detailed Analysis" tooltip="" />
            <Stack spacing={1.2} sx={{ mt: 1 }}>
              {/* Event Type */}
              <FancySkeleton height={14} width="24%" radius={8} />
              <FancySkeleton height={12} width="82%" radius={8} />
              <Box sx={{ height: 10 }} />
              {/* Audience Baseline */}
              <FancySkeleton height={14} width="28%" radius={8} />
              <FancySkeleton height={12} width="92%" radius={8} />
              <FancySkeleton height={12} width="86%" radius={8} />
              <Box sx={{ height: 10 }} />
              {/* Analysis */}
              <FancySkeleton height={14} width="16%" radius={8} />
              <FancySkeleton height={12} width="88%" radius={8} />
              <FancySkeleton height={12} width="78%" radius={8} />
              <FancySkeleton height={12} width="70%" radius={8} />
              <Box sx={{ height: 10 }} />
              {/* Media Description */}
              <FancySkeleton height={14} width="22%" radius={8} />
              <FancySkeleton height={12} width="90%" radius={8} />
              <Box sx={{ height: 10 }} />
              {/* Notable Deviations */}
              <FancySkeleton height={14} width="26%" radius={8} />
              <FancySkeleton height={12} width="40%" radius={8} />
              <Box sx={{ height: 10 }} />
              {/* Recommendations */}
              <FancySkeleton height={14} width="26%" radius={8} />
              <FancySkeleton height={12} width="92%" radius={8} />
              <FancySkeleton height={12} width="88%" radius={8} />
              <FancySkeleton height={12} width="62%" radius={8} />
            </Stack>
          </Box>

          {/* Sample reactions skeleton */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5 }}>Sample Reactions</Typography>
            <ReactionSkeletonList count={3} />
          </Box>
        </Stack>
      </Box>
    );
  }

  // Partial rendering while processing but forecast is ready
  if (isProcessing && hasForecast) {
    const forecast = displayResults?.data?.forecast;
    const formData = {
      content: simulation.content || '',
      platform: simulation.platform || '',
      sender: simulation.sender || '',
      sector: simulation.sector || ''
    };

    let repDelta: number;
    let uiScore: number;
    let reputationInfo: { level: string; color: string };
    if (forecast?.reputation) {
      repDelta = forecast.reputation.repDelta;
      uiScore = forecast.reputation.uiScore;
      reputationInfo = { level: forecast.reputation.level, color: forecast.reputation.color };
    } else {
      const calcResult = calcReputationDelta({
        positivePct: forecast?.positivePct || 0,
        negativePct: forecast?.negativePct || 0,
        confidence: forecast?.confidence || 'Low',
        delta: forecast?.delta
      });
      repDelta = calcResult.repDelta;
      uiScore = calcResult.uiScore;
      reputationInfo = getReputationLevel(repDelta);
    }

    return (
      <Box sx={{ my: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom color="text.primary">
            Preparing Your Simulation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Forecast is ready. We’re generating sample reactions now.
          </Typography>
        </Box>

        <Stack spacing={4}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: { xs: '100%', sm: '85%', md: '75%', lg: '60%' } }}>
              <ContentCard 
                content={formData.content}
                platform={formData.platform}
                sender={formData.sender}
                imageUrl={simulation.imageUrl || null}
                imageDescription={displayResults.imageDescription}
                replies7d={forecast?.replies7d}
                likes7d={forecast?.likes7d}
                titleComponent={<CardTitle title="Content Preview" tooltip="Your content preview." />}
              />
            </Box>
          </Box>

          <ResultsMetrics forecast={forecast} uiScore={uiScore} reputationInfo={reputationInfo} />

          {(() => {
            const img = displayResults.imageDescription as any;
            const imgDesc = typeof img === 'string' ? img : img?.alt || null;
            const deviations = typeof img === 'object' && Array.isArray(img?.notableDeviations) ? img.notableDeviations : [];
            return (
              <SummaryCard 
                driversPositive={forecast?.driversPositive || []}
                driversNegative={forecast?.driversNegative || []}
                notableDeviations={deviations}
                imageDescription={imgDesc}
                forecast={forecast}
                titleComponent={<CardTitle title="Detailed Analysis" tooltip="Key factors." />}
              />
            );
          })()}

          <Box>
            <CardTitle title="Sample Reactions" tooltip="Generated examples incoming..." />
            <ReactionSkeletonList count={3} />
            {/* Notification is enabled by default; no button needed */}
          </Box>
        </Stack>
      </Box>
    );
  }
  const formData = {
    content: simulation.content || '',
    platform: simulation.platform || '',
    sender: simulation.sender || '',
    sector: simulation.sector || ''
  };

  // Get the reputation data for display (now using backend calculation)
  const forecast = displayResults?.data?.forecast;
  let repDelta: number;
  let uiScore: number;
  let reputationInfo: {
    level: string;
    color: string;
  };
  
  // First check if we have the new reputation data from backend
  if (forecast?.reputation) {
    repDelta = forecast.reputation.repDelta;
    uiScore = forecast.reputation.uiScore;
    reputationInfo = {
      level: forecast.reputation.level,
      color: forecast.reputation.color
    };
  } else {
    // Fallback to frontend calculation for older simulations
    const calcResult = calcReputationDelta({
      positivePct: forecast?.positivePct || 0,
      negativePct: forecast?.negativePct || 0,
      confidence: forecast?.confidence || 'Low',
      delta: forecast?.delta
    });
    repDelta = calcResult.repDelta;
    uiScore = calcResult.uiScore;
    reputationInfo = getReputationLevel(repDelta);
  }

  return (
    <>
      {/* Alert message */}
      <Snackbar 
        open={!!alertMessage} 
        autoHideDuration={6000} 
        onClose={hideAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={hideAlert} 
          severity={alertMessage?.type || 'info'} 
          sx={{ width: '100%' }}
        >
          {alertMessage?.message || ''}
        </Alert>
      </Snackbar>
      
      {/* Trial Expired Modal */}
      <TrialStatusModal 
        isOpen={isExpiredModalOpen} 
        onClose={closeExpiredModal} 
        isAdmin={isAdmin}
        status="expired"
      />
      
      {/* Main Results Content */}
      {displayResults && simulation && (
        <Box sx={{ my: 4 }}>
          {/* Top Actions - Compact */}
          <ResultsActions
            simulation={simulation}
            effectiveSimId={effectiveSimId}
            isCreator={isCreator}
            loading={loading}
            displayResults={displayResults}
            formData={formData}
            forecast={forecast}
            reputationInfo={reputationInfo}
            uiScore={uiScore}
            onSimulationUpdate={updateSimulation}
            showSuccess={showSuccess}
            showAlert={showAlert}
            variant="compact"
          />

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom color="text.primary">
              Simulation Results
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Based on your input, we've simulated how your post would perform on {formData.platform} as {formData.sender}.
            </Typography>
          </Box>

          <Stack spacing={4}>
            {/* Content Card (displaying the simulated post) - Moved to the top */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ width: { xs: '100%', sm: '85%', md: '75%', lg: '60%' } }}>
                <ContentCard 
                  content={formData.content}
                  platform={formData.platform}
                  sender={formData.sender}
                  imageUrl={simulation.imageUrl || null}
                  imageDescription={displayResults.imageDescription}
                  replies7d={forecast?.replies7d}
                  likes7d={forecast?.likes7d}
                  titleComponent={
                    <CardTitle 
                      title="Content Preview" 
                      tooltip="A preview of the content you submitted for simulation. Engagement figures are forecasts based on historical averages. Actual results may vary with algorithm changes or ad spend."
                    />
                  }
                />
              </Box>
            </Box>
            
            {/* Charts Section */}
            <ResultsMetrics
              forecast={forecast}
              uiScore={uiScore}
              reputationInfo={reputationInfo}
            />
            
            {/* Detailed Analysis */}
            {(() => {
              const img = displayResults.imageDescription as any;
              const imgDesc = typeof img === 'string' ? img : img?.alt || null;
              const deviations = typeof img === 'object' && Array.isArray(img?.notableDeviations) ? img.notableDeviations : [];
              return (
                <SummaryCard 
                  driversPositive={forecast?.driversPositive || []}
                  driversNegative={forecast?.driversNegative || []}
                  notableDeviations={deviations}
                  imageDescription={imgDesc}
                  forecast={forecast}
                  titleComponent={
                    <CardTitle 
                      title="Detailed Analysis" 
                      tooltip="Key factors that influenced the sentiment forecast."
                    />
                  }
                />
              );
            })()}
            
            {/* Reactions Card */}
            <ReactionsCard 
              reactions={displayResults.data.simulation || []}
              positivePct={forecast?.positivePct}
              neutralPct={forecast?.neutralPct}
              negativePct={forecast?.negativePct}
              titleComponent={
                <CardTitle 
                  title="Sample Reactions" 
                  tooltip="Generated examples of how users might respond to this content."
                />
              }
            />
            
            {/* Action Buttons */}
            <ResultsActions
              simulation={simulation}
              effectiveSimId={effectiveSimId}
              isCreator={isCreator}
              loading={loading}
              displayResults={displayResults}
              formData={formData}
              forecast={forecast}
              reputationInfo={reputationInfo}
              uiScore={uiScore}
              onSimulationUpdate={updateSimulation}
              showSuccess={showSuccess}
              showAlert={showAlert}
              variant="full"
            />
          </Stack>
        </Box>
      )}
    </>
  );
};

export default ResultsContainer;
